import { Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ArrowUpRight } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { cn } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';
import { Eyebrow, Squiggle } from '../primitives';

type Solution = {
	badge: string;
	chip: string;
	title: string;
	description: string;
	photo: string;
	src: string;
	/**
	 * Draw `src` as the hand-off's inset browser panel, not a full-bleed photo: full-bleed read at twice the
	 * design's size and its page header collided with the tab.
	 */
	panel?: boolean;
};

// Every deck length lives in `.new-stack` in ../../-styles/theme.css: the scroll-driven depth only lines up if they all agree.
const CARD_HEIGHT = 'h-[var(--stack-card-h)]';

/**
 * The card's own proportion at `lg` (theme.css), because `Image` requests both dimensions with imgproxy
 * `rt:fill`: any other aspect is a first crop that `object-cover` crops again.
 */
const CARD_W = 1360;
const CARD_H = 480;

/**
 * The hand-off's `right: 56.64px; top: 56.75px; width: 738.731px` in a 1352x479 card, plus the 8.987px
 * stroke Figma draws outside it. The export is already clipped at the card's edge.
 */
const PANEL = { left: '40.5%', top: '9.94%', width: '55.99%' };
/** The export's exact proportion, so the CDN never crops it. */
const PANEL_W = 1514;
const PANEL_H = 863;

// Where a fully buried card ends up; the scroll timeline drives it there, so no listener and no spring lag.
const SHRINK_PER_LAYER = 0.04;
const MIN_SCALE = 0.88;
const DIM_PER_LAYER = 0.085;
const MAX_DIM = 0.26;

// The middle two photos deliberately differ from the hand-off (TEC-3830): docs/notes/portals/home-solutions.md

export function Solutions() {
	const { t } = useLingui();
	const solutions: Solution[] = [
		{
			badge: '1',
			chip: t`Physical stores`,
			title: t`Charity Rounding Up`,
			description: t`The customer adds a few cents to their card purchase and donates them to a social cause. Those cents add up and go to the solidarity project your company chooses.`,
			photo: t`A card terminal asking the customer to round up their purchase`,
			src: '/redesign/solution-pos-wide.jpg',
		},
		{
			badge: '2',
			chip: t`eCommerce`,
			title: t`Solidarity Euro`,
			description: t`A fixed microdonation or round-up one click before checkout. Without interrupting the flow, frictionless.`,
			photo: t`An online checkout offering the Solidarity Euro before payment`,
			src: '/redesign/solution-ecommerce-wide-v2.jpg',
		},
		{
			badge: '3',
			chip: t`Payroll & invoices rounding`,
			title: t`Payroll & invoices`,
			description: t`Automated recurring donations through the cents of salaries or monthly subscriptions.`,
			photo: t`Hands with a receipt and calculator`,
			src: '/redesign/solution-payroll-wide-v2.jpg',
		},
		{
			badge: '4',
			chip: t`Solidarity Portal`,
			title: t`Solidarity Portal`,
			description: t`A tailor-made platform so your employees can join corporate or emergency campaigns.`,
			photo: t`A company's solidarity portal showing a live campaign`,
			src: '/redesign/solution-portal-panel.png',
			panel: true,
		},
	];

	return (
		<section className="mx-auto max-w-[1440px] px-6 pt-8 pb-16 md:pb-24 xl:px-10">
			{/* Outside the sticky context, so the stack never depends on how many lines a locale wraps the title to. */}
			<div className="mx-auto mb-10 flex max-w-4xl flex-col items-center gap-4 text-center md:mb-14">
				<Eyebrow>{t`Our solutions`}</Eyebrow>
				<h2 className="text-3xl font-medium tracking-tight md:text-5xl">
					{t`How we generate`} <Squiggle>{t`impact`}</Squiggle> {t`on every purchase`}
				</h2>
				<p className="text-ink text-base md:text-lg">
					{t`We offer tailor-made tech solutions that integrate into your company's purchase flow, frictionless and fully transparent.`}
				</p>
			</div>

			{/* `new-stack` also names the view timeline, so one scroll drives the whole pile. */}
			<div className="new-stack flex flex-col gap-[var(--stack-gap)]">
				{solutions.map((s, i) => (
					<StackCard key={s.badge} solution={s} index={i} count={solutions.length} />
				))}
				{/* Lets the assembled deck hold and leave together; without it the top card is dragged off first. */}
				<div aria-hidden className="h-[8rem] md:h-[18rem]" />
			</div>
		</section>
	);
}

function StackCard(props: { solution: Solution; index: number; count: number }) {
	const { index, count } = props;
	const layers = count - 1 - index;

	const style = {
		// Keeps every parked card's tab readable as the deck piles up.
		'top': `calc(var(--stack-top) + ${index} * var(--stack-tab))`,
		// From the top edge, so a buried card reads as behind the one covering it, not merely smaller.
		'transformOrigin': 'center top',
		// theme.css turns these into the slice of scroll this card recedes over.
		'--stack-i': index,
		'--stack-last': count - 1,
		'--stack-end-scale': Math.max(MIN_SCALE, 1 - layers * SHRINK_PER_LAYER),
		'--stack-end-veil': Math.min(MAX_DIM, layers * DIM_PER_LAYER),
	} as CSSProperties;

	return (
		<div className="new-stack-card sticky" style={style}>
			<SplitCard solution={props.solution}>
				{/* Transparent until the animation drives it: without scroll timelines or with reduced motion it never shows. */}
				<div aria-hidden className="new-stack-veil bg-ink pointer-events-none absolute inset-0 opacity-0" />
			</SplitCard>
		</div>
	);
}

function SplitCard(props: { solution: Solution; children?: ReactNode }) {
	// The `t` macro only transforms a `t` from this component's own hook; a passed-in one renders empty.
	const { t } = useLingui();
	const s = props.solution;
	return (
		<article
			className={cn(
				// No background here: a second painted surface leaks into the arc's antialiasing (-114 measured, 0 without).
				'text-ink relative overflow-hidden rounded-3xl shadow-[0_-14px_40px_-16px_rgba(37,48,44,0.22)]',
				CARD_HEIGHT,
			)}
		>
			{/* One promoted layer (`transform-gpu`), or the corners trace a dark hairline: docs/notes/portals/home-solutions.md */}
			{/* The hand-off grounds the portal card in black, so the corner the sweep clears reads dark behind the panel. */}
			<div className={cn('absolute inset-0 transform-gpu', s.panel ? 'bg-black' : 'bg-apricot')}>
				{s.panel ? (
					// `<picture>` is inline (baseline gap, shrink-to-fit width). The export has no alpha, so its 25px
					// corners arrive as opaque #CCCCCC wedges the rounded clip trims; keying them out leaves a fringe.
					<div style={PANEL} className="absolute overflow-hidden rounded-[25px] [&>picture]:block">
						<Image
							src={s.src}
							alt={s.photo}
							height={PANEL_H / 2}
							aspectRatio={PANEL_W / PANEL_H}
							objectFit="contain"
							className="h-auto w-full"
						/>
					</div>
				) : (
					<Image
						src={s.src}
						alt={s.photo}
						height={CARD_H}
						aspectRatio={CARD_W / CARD_H}
						objectFit="cover"
						className="absolute inset-0 h-full w-full object-cover!"
					/>
				)}
				{/* On a phone a diagonal sweep would wash out the whole photo, so it fades from both edges instead. */}
				<div aria-hidden className="from-apricot absolute inset-0 bg-gradient-to-b to-transparent to-30% md:hidden" />
				<div
					aria-hidden
					className="from-apricot via-apricot/85 absolute inset-0 bg-gradient-to-t from-50% via-70% to-transparent md:hidden"
				/>
				{/* Fitted to the hand-off; `to-tr` follows the card's diagonal and only matched at `xl`. */}
				<div
					aria-hidden
					className="from-apricot absolute inset-0 hidden bg-linear-68 from-25% to-transparent md:block"
				/>

				<div className="relative flex h-full flex-col">
					{/* Its height is the strip left peeking when the next card stacks on top. */}
					<div className="flex h-[var(--stack-tab)] shrink-0 items-center gap-2.5 px-6">
						<span className="bg-brand grid size-6 shrink-0 place-items-center rounded-md text-sm font-semibold text-black tabular-nums">
							{s.badge}
						</span>
						<span className="text-ink text-sm font-medium">{s.chip}</span>
					</div>

					{/* The padding is the floor when a long locale leaves `my-auto` no room; `px-6` keeps the tab's left edge. */}
					<div className="my-auto max-w-xl px-6 py-8 md:py-12">
						<h3 className="text-3xl font-medium tracking-tight text-balance md:text-5xl">{s.title}</h3>
						<p className="text-ink mt-4 max-w-md md:text-lg">{s.description}</p>
					</div>

					<div className="px-6 pb-8 md:pb-12">
						<Link
							to="/www/contact"
							className="hover:text-brand-strong flex w-fit items-center gap-1.5 text-sm font-medium transition-colors"
						>
							{t`Contact us`}
							<ArrowUpRight className="size-4" />
						</Link>
					</div>
				</div>

				{props.children}
			</div>
		</article>
	);
}
